import {
  getBands,
  getAdminBands,
  getBandById,
  getBandsForDropdown,
  getFeaturedBands,
  createBand,
  editBand,
  deleteBand,
  toggleBandStatus,
  getTopBandsByRevenue,
  getBandAnalytics,
  getBandProductAnalytics,
  getProductsByBand,
  createDashboardAccess,
  resetDashboardPassword,
  removeDashboardAccess,
  getBandDetails,
  updateBandDetails,
  getBandPublicDetails,
  getBandByName,
} from "./asyncThunks";

export const getBandsBuilder = (builder) => {
  builder.addCase(getBands.pending, (state) => {
    state.bands.loading = true;
    state.bands.error = null;
  });
  builder.addCase(getBands.fulfilled, (state, { payload }) => {
    const { data, pagination, maxReached } = payload;
    
    // Parse JSON strings for each band
    const parsedData = data.map((band) => {
      let members = [];
      let albums = [];
      let achievements = [];
      let events = [];
      let stats = {};
      
      // Parse members safely
      if (typeof band.members === 'string') {
        try {
          members = JSON.parse(band.members);
        } catch (e) {
          console.error('Error parsing members for band', band.name, e);
          members = [];
        }
      } else {
        members = band.members || [];
      }
      
      // Parse discography safely
      if (typeof band.discography === 'string') {
        try {
          albums = JSON.parse(band.discography);
        } catch (e) {
          console.error('Error parsing discography for band', band.name, e);
          albums = [];
        }
      } else {
        albums = band.albums || band.discography || [];
      }
      
      // Parse achievements safely
      if (typeof band.achievements === 'string') {
        try {
          achievements = JSON.parse(band.achievements);
        } catch (e) {
          console.error('Error parsing achievements for band', band.name, e);
          achievements = [];
        }
      } else {
        achievements = band.achievements || [];
      }
      
      // Parse events safely
      if (typeof band.events === 'string') {
        try {
          events = JSON.parse(band.events);
        } catch (e) {
          console.error('Error parsing events for band', band.name, e);
          events = [];
        }
      } else {
        events = band.events || [];
      }
      
      // Parse stats safely
      if (typeof band.stats === 'string') {
        try {
          stats = JSON.parse(band.stats);
        } catch (e) {
          console.error('Error parsing stats for band', band.name, e);
          stats = {};
        }
      } else {
        stats = band.stats || {};
      }
      
      return {
        ...band,
        members,
        albums,
        achievements,
        events,
        stats,
        products: band.numOfProducts || band.products || 0,
      };
    });
    
    // For page-based pagination, always replace data with current page data
    state.bands.data = parsedData;
    
    state.bands.pagination = pagination;
    state.bands.maxReached = maxReached;
    state.bands.loading = false;
  });
  builder.addCase(getBands.rejected, (state, action) => {
    state.bands.loading = false;
    state.bands.error = action.error.message;
  });
};

export const getAdminBandsBuilder = (builder) => {
  builder.addCase(getAdminBands.pending, (state) => {
    state.bands.loading = true;
    state.bands.error = null;
  });
  builder.addCase(getAdminBands.fulfilled, (state, { payload }) => {
    const { data, pagination, maxReached } = payload;
    
    // Parse JSON strings for each band
    const parsedData = data.map((band) => {
      let members = [];
      let albums = [];
      let achievements = [];
      let events = [];
      let stats = {};
      
      // Parse members safely
      if (typeof band.members === 'string') {
        try {
          members = JSON.parse(band.members);
        } catch (e) {
          console.error('Error parsing members for band', band.name, e);
          members = [];
        }
      } else {
        members = band.members || [];
      }
      
      // Parse discography safely
      if (typeof band.discography === 'string') {
        try {
          albums = JSON.parse(band.discography);
        } catch (e) {
          console.error('Error parsing discography for band', band.name, e);
          albums = [];
        }
      } else {
        albums = band.albums || band.discography || [];
      }
      
      // Parse achievements safely
      if (typeof band.achievements === 'string') {
        try {
          achievements = JSON.parse(band.achievements);
        } catch (e) {
          console.error('Error parsing achievements for band', band.name, e);
          achievements = [];
        }
      } else {
        achievements = band.achievements || [];
      }
      
      // Parse events safely
      if (typeof band.events === 'string') {
        try {
          events = JSON.parse(band.events);
        } catch (e) {
          console.error('Error parsing events for band', band.name, e);
          events = [];
        }
      } else {
        events = band.events || [];
      }
      
      // Parse stats safely
      if (typeof band.stats === 'string') {
        try {
          stats = JSON.parse(band.stats);
        } catch (e) {
          console.error('Error parsing stats for band', band.name, e);
          stats = {};
        }
      } else {
        stats = band.stats || {};
      }
      
      return {
        ...band,
        members,
        albums,
        achievements,
        events,
        stats,
        products: band.numOfProducts || band.products || 0,
      };
    });
    
    // Always replace data for admin pages (no pagination concatenation)
    state.bands.data = parsedData;
    state.bands.pagination = pagination;
    state.bands.maxReached = maxReached;
    state.bands.loading = false;
  });
  builder.addCase(getAdminBands.rejected, (state, action) => {
    state.bands.loading = false;
    state.bands.error = action.error.message;
  });
};

export const getBandByIdBuilder = (builder) => {
  builder.addCase(getBandById.pending, (state) => {
    state.bandById.loading = true;
    state.bandById.error = null;
  });
  builder.addCase(getBandById.fulfilled, (state, { payload }) => {
    const { data } = payload;
    state.bandById.data = data;
    state.bandById.loading = false;
  });
  builder.addCase(getBandById.rejected, (state, action) => {
    state.bandById.loading = false;
    state.bandById.error = action.error.message;
  });
};

export const getBandsForDropdownBuilder = (builder) => {
  builder.addCase(getBandsForDropdown.pending, (state) => {
    state.bandsDropdown.loading = true;
    state.bandsDropdown.error = null;
  });
  builder.addCase(getBandsForDropdown.fulfilled, (state, { payload }) => {
    const { data } = payload;
    state.bandsDropdown.data = data;
    state.bandsDropdown.loading = false;
  });
  builder.addCase(getBandsForDropdown.rejected, (state, action) => {
    state.bandsDropdown.loading = false;
    state.bandsDropdown.error = action.error.message;
  });
};

export const getFeaturedBandsBuilder = (builder) => {
  builder.addCase(getFeaturedBands.pending, (state) => {
    state.featuredBands.loading = true;
    state.featuredBands.error = null;
  });
  builder.addCase(getFeaturedBands.fulfilled, (state, { payload }) => {
    const { data } = payload;
    state.featuredBands.data = data;
    state.featuredBands.loading = false;
  });
  builder.addCase(getFeaturedBands.rejected, (state, action) => {
    state.featuredBands.loading = false;
    state.featuredBands.error = action.error.message;
  });
};

export const getTopBandsByRevenueBuilder = (builder) => {
  builder.addCase(getTopBandsByRevenue.pending, (state) => {
    state.bandsByRevenue.loading = true;
    state.bandsByRevenue.error = null;
  });
  builder.addCase(getTopBandsByRevenue.fulfilled, (state, { payload }) => {
    const { data } = payload;
    state.bandsByRevenue.data = data;
    state.bandsByRevenue.loading = false;
  });
  builder.addCase(getTopBandsByRevenue.rejected, (state, action) => {
    state.bandsByRevenue.loading = false;
    state.bandsByRevenue.error = action.error.message;
  });
};

export const createBandBuilder = (builder) => {
  builder.addCase(createBand.pending, (state) => {
    state.bands.createLoading = true;
    state.bands.error = null;
  });
  builder.addCase(createBand.fulfilled, (state, { payload }) => {
    const { data } = payload;
    state.bands.data = [data, ...state.bands.data]; // Dodaj na početak liste
    state.bands.createLoading = false;
  });
  builder.addCase(createBand.rejected, (state, action) => {
    state.bands.createLoading = false;
    state.bands.error = action.error.message;
  });
};

export const editBandBuilder = (builder) => {
  builder.addCase(editBand.pending, (state) => {
    state.bands.editLoading = true;
    state.bands.error = null;
  });
  builder.addCase(editBand.fulfilled, (state, { payload }) => {
    const { data } = payload;
    // Ažuriraj band u listi
    state.bands.data = state.bands.data.map((band) =>
      band.id === data.id ? data : band
    );
    // Ažuriraj i u bandById ako je učitan
    if (state.bandById.data && state.bandById.data.id === data.id) {
      state.bandById.data = data;
    }
    // Ažuriraj i u dropdown ako postoji
    state.bandsDropdown.data = state.bandsDropdown.data.map((band) =>
      band.id === data.id ? { id: data.id, name: data.name } : band
    );
    state.bands.editLoading = false;
  });
  builder.addCase(editBand.rejected, (state, action) => {
    state.bands.editLoading = false;
    state.bands.error = action.error.message;
  });
};

export const deleteBandBuilder = (builder) => {
  builder.addCase(deleteBand.pending, (state) => {
    state.bands.deleteLoading = true;
    state.bands.error = null;
  });
  builder.addCase(deleteBand.fulfilled, (state, { payload }) => {
    const { data } = payload;
    // Ukloni bend iz liste
    state.bands.data = state.bands.data.filter((band) => band.id !== data.id);
    // Ukloni iz dropdown-a
    state.bandsDropdown.data = state.bandsDropdown.data.filter(
      (band) => band.id !== data.id
    );
    // Očisti bandById ako je obrisan bend učitan
    if (state.bandById.data && state.bandById.data.id === data.id) {
      state.bandById.data = null;
    }
    state.bands.deleteLoading = false;
  });
  builder.addCase(deleteBand.rejected, (state, action) => {
    state.bands.deleteLoading = false;
    state.bands.error = action.error.message;
  });
};

export const toggleBandStatusBuilder = (builder) => {
  builder.addCase(toggleBandStatus.pending, (state) => {
    state.bands.toggleLoading = true;
    state.bands.error = null;
  });
  builder.addCase(toggleBandStatus.fulfilled, (state, { payload }) => {
    const { data } = payload;
    // Ažuriraj status u listi
    state.bands.data = state.bands.data.map((band) =>
      band.id === data.id ? data : band
    );
    // Ažuriraj u bandById ako je učitan
    if (state.bandById.data && state.bandById.data.id === data.id) {
      state.bandById.data = data;
    }
    state.bands.toggleLoading = false;
  });
  builder.addCase(toggleBandStatus.rejected, (state, action) => {
    state.bands.toggleLoading = false;
    state.bands.error = action.error.message;
  });
};

// BUILDER-I ZA DASHBOARD PRISTUP

export const createDashboardAccessBuilder = (builder) => {
  builder.addCase(createDashboardAccess.pending, (state) => {
    state.dashboardAccess.createLoading = true;
    state.dashboardAccess.error = null;
  });
  builder.addCase(createDashboardAccess.fulfilled, (state, { payload }) => {
    const { data } = payload;

    // Ažuriraj bend u listi da označi da ima pristup
    state.bands.data = state.bands.data.map((band) => {
      if (band.id === data.bandId) {
        return {
          ...band,
          adminCredentials: {
            username: data.username,
            hasAccess: true,
          },
        };
      }
      return band;
    });

    // Ažuriraj i u bandById ako je trenutno učitan
    if (state.bandById.data && state.bandById.data.id === data.bandId) {
      state.bandById.data = {
        ...state.bandById.data,
        adminCredentials: {
          username: data.username,
          hasAccess: true,
        },
      };
    }

    state.dashboardAccess.createLoading = false;
    state.dashboardAccess.lastCreated = data;
  });
  builder.addCase(createDashboardAccess.rejected, (state, action) => {
    state.dashboardAccess.createLoading = false;
    state.dashboardAccess.error = action.error.message;
  });
};

export const resetDashboardPasswordBuilder = (builder) => {
  builder.addCase(resetDashboardPassword.pending, (state) => {
    state.dashboardAccess.resetLoading = true;
    state.dashboardAccess.error = null;
  });
  builder.addCase(resetDashboardPassword.fulfilled, (state, { payload }) => {
    const { data } = payload;

    state.dashboardAccess.resetLoading = false;
    state.dashboardAccess.lastReset = {
      bandId: data.bandId,
      username: data.username,
      timestamp: new Date().toISOString(),
    };
  });
  builder.addCase(resetDashboardPassword.rejected, (state, action) => {
    state.dashboardAccess.resetLoading = false;
    state.dashboardAccess.error = action.error.message;
  });
};

export const removeDashboardAccessBuilder = (builder) => {
  builder.addCase(removeDashboardAccess.pending, (state) => {
    state.dashboardAccess.removeLoading = true;
    state.dashboardAccess.error = null;
  });
  builder.addCase(removeDashboardAccess.fulfilled, (state, { payload }) => {
    const { data } = payload;

    // Ukloni pristup iz benda u listi
    state.bands.data = state.bands.data.map((band) => {
      if (band.id === data.bandId) {
        return {
          ...band,
          adminCredentials: null,
        };
      }
      return band;
    });

    // Ukloni i iz bandById ako je trenutno učitan
    if (state.bandById.data && state.bandById.data.id === data.bandId) {
      state.bandById.data = {
        ...state.bandById.data,
        adminCredentials: null,
      };
    }

    state.dashboardAccess.removeLoading = false;
    state.dashboardAccess.lastRemoved = data;
  });
  builder.addCase(removeDashboardAccess.rejected, (state, action) => {
    state.dashboardAccess.removeLoading = false;
    state.dashboardAccess.error = action.error.message;
  });
};

// ANALYTICS

export const getBandAnalyticsBuilder = (builder) => {
  builder.addCase(getBandAnalytics.pending, (state) => {
    state.bandAnalytics.loading = true;
    state.bandAnalytics.error = null;
  });
  builder.addCase(getBandAnalytics.fulfilled, (state, { payload }) => {
    const { data } = payload;
    state.bandAnalytics.data = data;
    state.bandAnalytics.loading = false;
  });
  builder.addCase(getBandAnalytics.rejected, (state, action) => {
    state.bandAnalytics.loading = false;
    state.bandAnalytics.error = action.error.message;
  });
};

export const getBandProductAnalyticsBuilder = (builder) => {
  builder.addCase(getBandProductAnalytics.pending, (state) => {
    state.bandProductAnalytics.loading = true;
    state.bandProductAnalytics.error = null;
  });
  builder.addCase(getBandProductAnalytics.fulfilled, (state, { payload }) => {
    const { data } = payload;
    state.bandProductAnalytics.data = data;
    state.bandProductAnalytics.loading = false;
  });
  builder.addCase(getBandProductAnalytics.rejected, (state, action) => {
    state.bandProductAnalytics.loading = false;
    state.bandProductAnalytics.error = action.error.message;
  });
};

export const getProductsByBandBuilder = (builder) => {
  builder.addCase(getProductsByBand.pending, (state) => {
    state.bandProducts.loading = true;
    state.bandProducts.error = null;
  });
  builder.addCase(getProductsByBand.fulfilled, (state, { payload }) => {
    const { data, total, page, perPage, maxReached, band } = payload;
    state.bandProducts.data = data;
    state.bandProducts.total = total;
    state.bandProducts.page = page;
    state.bandProducts.perPage = perPage;
    state.bandProducts.maxReached = maxReached;
    state.bandProducts.band = band;
    state.bandProducts.loading = false;
  });
  builder.addCase(getProductsByBand.rejected, (state, action) => {
    state.bandProducts.loading = false;
    state.bandProducts.error = action.error.message;
  });
};

export const getBandDetailsBuilder = (builder) => {
  builder.addCase(getBandDetails.pending, (state) => {
    state.bandDetails.loading = true;
    state.bandDetails.error = null;
  });
  builder.addCase(getBandDetails.fulfilled, (state, { payload }) => {
    state.bandDetails.data = payload.data || payload;
    state.bandDetails.loading = false;
  });
  builder.addCase(getBandDetails.rejected, (state, action) => {
    state.bandDetails.loading = false;
    state.bandDetails.error = action.payload || action.error.message;
  });
};

export const updateBandDetailsBuilder = (builder) => {
  builder.addCase(updateBandDetails.pending, (state) => {
    state.bandDetails.updateLoading = true;
    state.bandDetails.error = null;
  });
  builder.addCase(updateBandDetails.fulfilled, (state, { payload }) => {
    // : Backend vraća { message: "...", data: {...} }
    const updatedData = payload.data || payload;
    state.bandDetails.data = updatedData;
    state.bandDetails.updateLoading = false;

    // Ažuriraj i u osnovnoj listi bendova ako postoji
    if (state.bands.data.length > 0) {
      state.bands.data = state.bands.data.map((band) =>
        band.id === updatedData.id ? { ...band, ...updatedData } : band
      );
    }

    // Ažuriraj u bandById ako je učitan
    if (state.bandById.data && state.bandById.data.id === updatedData.id) {
      state.bandById.data = { ...state.bandById.data, ...updatedData };
    }
  });
  builder.addCase(updateBandDetails.rejected, (state, action) => {
    state.bandDetails.updateLoading = false;
    state.bandDetails.error = action.payload || action.error.message;
  });
};

export const getBandPublicDetailsBuilder = (builder) => {
  builder.addCase(getBandPublicDetails.pending, (state) => {
    state.publicBandDetails.loading = true;
    state.publicBandDetails.error = null;
  });
  builder.addCase(getBandPublicDetails.fulfilled, (state, { payload }) => {
    const { data } = payload;
    
    // Parse JSON strings safely
    const parsedData = { ...data };
    
    if (typeof data.members === 'string') {
      try {
        parsedData.members = JSON.parse(data.members);
      } catch (e) {
        console.error('Error parsing members for band', data.name, e);
        parsedData.members = [];
      }
    }
    
    if (typeof data.discography === 'string') {
      try {
        parsedData.discography = JSON.parse(data.discography);
      } catch (e) {
        console.error('Error parsing discography for band', data.name, e);
        parsedData.discography = [];
      }
    }
    
    if (typeof data.achievements === 'string') {
      try {
        parsedData.achievements = JSON.parse(data.achievements);
      } catch (e) {
        console.error('Error parsing achievements for band', data.name, e);
        parsedData.achievements = [];
      }
    }
    
    if (typeof data.events === 'string') {
      try {
        parsedData.events = JSON.parse(data.events);
      } catch (e) {
        console.error('Error parsing events for band', data.name, e);
        parsedData.events = [];
      }
    }
    
    if (typeof data.stats === 'string') {
      try {
        parsedData.stats = JSON.parse(data.stats);
      } catch (e) {
        console.error('Error parsing stats for band', data.name, e);
        parsedData.stats = {};
      }
    }
    
    // Parse social links if they're in bandImages or separate fields
    if (data.fbUrl || data.instaUrl || data.ytUrl || data.spotifyUrl || data.appleMusicUrl || data.soundcloudUrl || data.xUrl || data.websiteUrl) {
      parsedData.socialLinks = {
        facebook: data.fbUrl,
        instagram: data.instaUrl,
        youtube: data.ytUrl,
        spotify: data.spotifyUrl,
        apple: data.appleMusicUrl,
        soundcloud: data.soundcloudUrl,
        twitter: data.xUrl,
        website: data.websiteUrl,
      };
    }
    
    // Set gallery from bandImages
    if (data.bandImages && Array.isArray(data.bandImages)) {
      parsedData.gallery = data.bandImages;
    }
    
    state.publicBandDetails.data = parsedData;
    state.publicBandDetails.loading = false;
  });
  builder.addCase(getBandPublicDetails.rejected, (state, action) => {
    state.publicBandDetails.loading = false;
    state.publicBandDetails.error = action.error.message;
  });
};

export const getBandByNameBuilder = (builder) => {
  builder.addCase(getBandByName.pending, (state) => {
    state.bandByName.loading = true;
    state.bandByName.error = null;
  });
  builder.addCase(getBandByName.fulfilled, (state, { payload }) => {
    const { data } = payload;
    state.bandByName.data = data;
    state.bandByName.loading = false;
  });
  builder.addCase(getBandByName.rejected, (state, action) => {
    state.bandByName.loading = false;
    state.bandByName.error = action.error.message;
  });
};
